// API layer for Invitation - Internal (Panitia Lelang).
//
// TODO(API): no backend contract for this screen yet, so every function resolves
// from the in-memory store instead of the network. The interface is the shape
// the live layer will have — async, pagination in / `{ items, totalCount }` out,
// rejects with a string message the way crude_api_helper does — so wiring the
// real endpoints means editing only this file, not the views.
//
// When the contract arrives, expect roughly:
//   list        -> GET  {crudeHost}/v1/InvitationInternal        (page/pageSize/searchText/sortField/sortOrder)
//   getDetail   -> GET  {crudeHost}/v1/InvitationInternal/{id}
//   confirm     -> POST {crudeHost}/v1/InvitationInternal/{id}/Confirm  { mode, name, email }
//   downloadDoc -> GET  {crudeHost}/v1/InvitationInternal/{id}/Document/{documentId}/Download  (blob)
// Confirm the host with curl before wiring (AD-005). `sortField` here is the UI's
// camelCase key; the live version needs the PascalCase server-whitelisted
// spelling — keep that translation in this file so the views stay unchanged.

import * as store from './invitationInternalStore';
import { DEFAULT_PAGE_SIZE } from './invitationInternalConstants';

// Simulated latency so loading states actually render in dev.
const LATENCY_MS = 150;

const settle = (producer, delay = LATENCY_MS) =>
  new Promise((resolve, reject) => {
    setTimeout(() => {
      try {
        resolve(producer());
      } catch (error) {
        // crude_api_helper rejects with a plain string; callers do
        // `toast.error(`...: ${error}`)`. Match that so nothing changes later.
        reject(error.message || String(error));
      }
    }, delay);
  });

// List (paginated + searched + sorted server-side, as the real endpoint will be).
export const listInvitations = ({
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
  searchText = '',
  sortField = null,
  sortOrder = 'asc',
} = {}) =>
  settle(() =>
    store.list({
      search: searchText,
      page,
      pageSize,
      sortField,
      sortDir: sortOrder === 'desc' ? 'desc' : 'asc',
    })
  );

// Detail — rejects 'Invitation not found' so the view can show its not-found
// panel, mirroring InvitationExternal / TenderOpening.
export const getInvitation = id =>
  settle(() => {
    const found = store.getById(id);
    if (!found) throw new Error('Invitation not found');
    return found;
  });

// Confirm — validation lives in the store, so the Present/Delegate rules hold
// even if a caller forgets to check.
export const confirmInvitation = (id, participation) =>
  settle(() => store.confirm(id, participation));

// Documents: preview needs the file bytes, which do not exist while the module
// is dummy-backed. Reject with a clear message so the view can toast it instead
// of pretending to succeed.
export const downloadDocument = (invitationId, documentId) =>
  settle(() => {
    const record = store.getById(invitationId);
    const doc = (record?.supportingDocuments || []).find(
      d => d.id === documentId
    );
    if (!doc) throw new Error('Document not found');
    throw new Error(
      `No file storage wired yet for "${doc.fileName}" — pending the backend contract`
    );
  });
